import {
  getTeam,
  getTeamPermissions,
} from "@/gen_src/proto/chalk/server/v1/team-TeamService_connectquery";
import type { GetTeamResponse } from "@/gen_src/proto/chalk/server/v1/team_pb";
import { getConnectQueryKeyOptions } from "@/utils/connect-query/query-key";
import { useRouteInfo } from "@/utils/hooks/url/use-route-info";
import type { DescMethodUnary } from "@bufbuild/protobuf";
import type { Query, QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";

export const DEFAULT_ENVIRONMENT_CHANGE_RESET_PRESERVED_METHODS = [
  getTeam,
  getTeamPermissions,
] satisfies readonly DescMethodUnary[];

export interface UseResetStoreOnEnvironmentChangeOptions {
  preservedMethods?: readonly DescMethodUnary[];
}

export interface MakeEnvironmentChangeResetPredicateOptions {
  queryClient: QueryClient;
  preservedMethods?: readonly DescMethodUnary[];
  projectId?: string;
  environmentId?: string;
}

function getMethodKey(serviceName: string, methodName: string): string {
  return `${serviceName}/${methodName}`;
}

function isQueryForMethod(query: Query, method: DescMethodUnary): boolean {
  const queryKeyOptions = getConnectQueryKeyOptions(query.queryKey);
  return (
    queryKeyOptions?.serviceName === method.parent.typeName &&
    queryKeyOptions.methodName === method.name
  );
}

function getCachedGetTeamResponse(queryClient: QueryClient): GetTeamResponse | null {
  const getTeamQuery = queryClient
    .getQueryCache()
    .findAll({ predicate: (query) => isQueryForMethod(query, getTeam) })
    .find((query) => (query.state.data as GetTeamResponse | undefined)?.team != null);

  return (getTeamQuery?.state.data as GetTeamResponse | undefined) ?? null;
}

function canPreserveTeamScopedMethods({
  queryClient,
  projectId,
  environmentId,
}: {
  queryClient: QueryClient;
  projectId?: string;
  environmentId?: string;
}): boolean {
  if (projectId == null && environmentId == null) {
    return true;
  }

  const team = getCachedGetTeamResponse(queryClient)?.team;
  if (team == null) {
    return false;
  }

  const matchingProjects =
    projectId != null ? team.projects.filter((project) => project.id === projectId) : team.projects;

  if (projectId != null && matchingProjects.length === 0) {
    return false;
  }

  if (
    environmentId != null &&
    !matchingProjects.some((project) =>
      project.environments.some((environment) => environment.id === environmentId),
    )
  ) {
    return false;
  }

  return true;
}

export function makeEnvironmentChangeResetPredicate({
  queryClient,
  preservedMethods = DEFAULT_ENVIRONMENT_CHANGE_RESET_PRESERVED_METHODS,
  projectId,
  environmentId,
}: MakeEnvironmentChangeResetPredicateOptions): (query: Query) => boolean {
  const preservedMethodKeys = new Set(
    preservedMethods.map((method) => getMethodKey(method.parent.typeName, method.name)),
  );
  const canPreserveWhitelistedMethods = canPreserveTeamScopedMethods({
    queryClient,
    projectId,
    environmentId,
  });

  return (query) => {
    const queryKeyOptions = getConnectQueryKeyOptions(query.queryKey);
    if (queryKeyOptions?.methodName == null) {
      return true;
    }

    const isPreservedMethod = preservedMethodKeys.has(
      getMethodKey(queryKeyOptions.serviceName, queryKeyOptions.methodName),
    );

    return !isPreservedMethod || !canPreserveWhitelistedMethods;
  };
}

/**
 * Clear the grpc query client cache and rerun all active (watched) queries when the environment changes.
 *
 * This is designed to be a singleton, only used once in the root of the project in _app.tsx,
 * rendered by the NetworkingWrapper.
 */
export function useResetStoreOnEnvironmentChange(
  grpcQueryClient: QueryClient,
  {
    preservedMethods = DEFAULT_ENVIRONMENT_CHANGE_RESET_PRESERVED_METHODS,
  }: UseResetStoreOnEnvironmentChangeOptions = {},
) {
  const { environmentId, projectId } = useRouteInfo();

  const envRef = useRef(environmentId);

  useEffect(() => {
    async function stopQueriesAndReset() {
      if (environmentId != null && envRef.current != null && environmentId !== envRef.current) {
        const resetFilters = {
          predicate: makeEnvironmentChangeResetPredicate({
            queryClient: grpcQueryClient,
            preservedMethods,
            projectId,
            environmentId,
          }),
        };

        await grpcQueryClient.cancelQueries(resetFilters);
        await grpcQueryClient.resetQueries(resetFilters);
        envRef.current = environmentId;
      }
      if (environmentId != null && environmentId !== envRef.current) {
        envRef.current = environmentId;
      }
    }
    stopQueriesAndReset();
  }, [environmentId, grpcQueryClient, preservedMethods, projectId]);
}
